import React, { useEffect, useState } from 'react';
import { getInstagramCommentContext, type InstagramCommentData, type InstagramCommentContext,
  type InstagramCommentScope } from '../../services/instagram.ts';
import { useTheme } from '../../contexts/ThemeContext.tsx';

export function InstagramCommentCard({ comment, timestamp, scope, canReply, onReply }: {
  comment: InstagramCommentData; timestamp: string; scope?: InstagramCommentScope;
  canReply?: boolean; onReply?: (comment: InstagramCommentData) => void;
}) {
  const { isDark } = useTheme();
  const [context, setContext] = useState<InstagramCommentContext | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true; setContext(null); setLoading(true);
    if (!scope) { setLoading(false); return; }
    getInstagramCommentContext(comment.id, scope).then(value => {
      if (active && value.comment_id === comment.id) setContext(value);
    }).catch(() => { /* Persisted comment remains readable without the publication. */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [comment.id, scope?.contactId, scope?.identityId]);
  const surface = comment.is_public_reply ? (isDark ? 'bg-[#143A6B]' : 'bg-sminy-blue-light')
    : isDark ? 'bg-[#0F1D31]' : 'bg-white';
  const unavailable = comment.status !== 'available' || (!loading && context?.status !== 'available');
  const permalink = context?.permalink && /^https:\/\/(www\.)?instagram\.com\//.test(context.permalink) ? context.permalink : null;
  return <article className={`w-full min-w-0 max-w-[452px] rounded-xl border p-4 text-[13px] ${surface} ${isDark ? 'border-sminy-text-secondary text-slate-50' : 'border-sminy-border text-sminy-text-primary'}`}>
    <p className="text-xs font-semibold">{comment.is_public_reply ? 'Resposta pública' : 'Comentário público'} · {timestamp}</p>
    <div className={`my-3 rounded-lg px-3 py-2 text-xs ${isDark ? 'bg-[#14243A] text-[#98A2B3]' : 'bg-sminy-blue-soft text-sminy-muted'}`}>
      <p>{(context?.media_product_type || comment.media_product_type) === 'REELS' ? 'Reel' : 'Publicação'}{context?.caption ? ` · ${context.caption}` : ''}</p>
      {loading ? <p role="status" className="mt-1">Carregando publicação…</p>
        : permalink ? <a className="mt-1 inline-block text-sminy-primary hover:underline" href={permalink} target="_blank" rel="noopener noreferrer">Ver publicação</a>
          : <p className="mt-1">Publicação indisponível</p>}
    </div>
    <p className="whitespace-pre-wrap break-words">{comment.text || 'Comentário sem texto'}</p>
    {!comment.is_public_reply && <button type="button" className="mt-3 min-h-11 text-xs font-semibold text-sminy-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
      disabled={!canReply || loading || unavailable || !onReply} onClick={() => onReply?.(comment)}>
      {comment.status !== 'available' ? 'Comentário indisponível' : 'Responder comentário'}
    </button>}
  </article>;
}
